'use client'

import { useState } from 'react'
import { POSTER_PLACEHOLDER } from '@/lib/utils'
import { cn } from '@/lib/utils'

interface PosterImageProps {
  src: string | null | undefined
  alt: string
  className?: string
  /** aspect ratio class, defaults to 2:3 poster */
  ratio?: string
  eager?: boolean
}

/**
 * Lazy-loaded image with a graceful placeholder fallback.
 * Never shows a broken image icon - swaps to the branded placeholder on error.
 */
export function PosterImage({ src, alt, className, ratio = 'aspect-[2/3]', eager }: PosterImageProps) {
  const initial = src && src.startsWith('http') ? src : POSTER_PLACEHOLDER
  const [current, setCurrent] = useState(initial)
  const [loaded, setLoaded] = useState(false)

  return (
    <div className={cn('relative overflow-hidden bg-ink-700', ratio, className)}>
      {!loaded && <div className="absolute inset-0 skeleton" />}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={current}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          if (current !== POSTER_PLACEHOLDER) setCurrent(POSTER_PLACEHOLDER)
          setLoaded(true)
        }}
        className={cn(
          'h-full w-full object-cover transition-opacity duration-500',
          loaded ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  )
}
